import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Button, Checkbox, Empty, Modal, Segmented, Space, Spin, Tag, Tooltip, Typography} from 'antd';
import {
    CheckCircleOutlined,
    CheckSquareFilled,
    CheckSquareOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    CompassOutlined,
    EyeFilled,
    EyeOutlined,
    FileTextOutlined,
    HistoryOutlined,
    HourglassOutlined,
    MinusSquareFilled,
    MinusSquareOutlined,
    PauseCircleOutlined,
    QuestionCircleOutlined,
    RadarChartOutlined,
    ReloadOutlined,
    SwapOutlined,
} from '@ant-design/icons';
import LightweightCandlestickChart, {KLineBar} from '../../components/charts/LightweightCandlestickChart';
import CompactIndicatorBar, {INDICATOR_PALETTES} from '../trading/components/CompactIndicatorBar';
import {IndicatorConfig, TechnicalIndicator} from '../../hooks/useChartState';
import {tradingService} from '../../services/tradingService';
import TrackEvolutionEventCard from './TrackEvolutionEventCard';
import TrackEvolutionTimeline from './TrackEvolutionTimeline';
import {TrackEvolutionEvent, TrackEvolutionState, TrackForecastBatch} from './trackEvolutionTypes';
import {useTrackEvolution} from './hooks/useTrackEvolution';
import {useSerialPolling} from './hooks/useSerialPolling';
import './TrackEvolutionModal.css';

const {Text} = Typography;
const FORECAST_COLORS = ['#13c2c2', '#597ef7', 'var(--pt-amber)', '#eb2f96', 'var(--pt-bull)', '#9254de', '#f5222d', '#2f54eb'];

// 生命周期状态 → 图标/颜色/名称映射：图例紧凑化，EXPIRED 等状态用图标表达，悬浮显示状态名（2026-08-01）
const LIFECYCLE_STATUS_META: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
    HISTORICAL: {icon: <HistoryOutlined/>, color: '#8c8c8c', label: '历史批次'},
    RAW: {icon: <FileTextOutlined/>, color: '#8c8c8c', label: '原始批次'},
    INVALID: {icon: <CloseCircleOutlined/>, color: 'var(--pt-bear)', label: '无效'},
    VALIDATED: {icon: <CheckCircleOutlined/>, color: 'var(--pt-bull)', label: '已验证'},
    CANDIDATE: {icon: <ClockCircleOutlined/>, color: 'var(--pt-amber)', label: '候选'},
    ACTIVE: {icon: <RadarChartOutlined/>, color: '#13c2c2', label: '已生效'},
    SUPERSEDED: {icon: <SwapOutlined/>, color: '#8c8c8c', label: '已被取代'},
    NOT_ACTIVATED: {icon: <PauseCircleOutlined/>, color: '#d9d9d9', label: '未启用'},
    EXPIRED: {icon: <HourglassOutlined/>, color: 'var(--pt-amber)', label: '已过期'},
};
const LIFECYCLE_STATUS_DEFAULT = {icon: <QuestionCircleOutlined/>, color: '#8c8c8c', label: '未知状态'};

function lifecycleStatusIcon(status: string): React.ReactNode {
    const meta = LIFECYCLE_STATUS_META[status] ?? LIFECYCLE_STATUS_DEFAULT;
    return (
        <Tooltip title={`${meta.label}（${status}）`}>
            <span className="track-evolution-legend-status" style={{color: meta.color}}>{meta.icon}</span>
        </Tooltip>
    );
}

// 初始化：主图指标(EMA/BOLL)激活，副图指标(RSI/MACD/KDJ)非激活，减少图表噪音
const DEFAULT_VISIBLE_INDICATORS: Record<string, boolean> = {
    EMA_12: true,
    EMA_26: true,
    RSI_9: false,
    RSI_14: false,
    BOLL_20: true,
    MACD_12_26_9: false,
    KDJ_9: false,
    KDJ_14: false,
    KDJ_21: false,
};
const TRACK_INDICATOR_TYPES: TechnicalIndicator[] = ['EMA', 'RSI', 'BOLL', 'MACD', 'KDJ'];

interface Props {
    open: boolean;
    onClose: () => void;
    trackUnitId: number;
    focusRunId?: number;
}

function eventKey(event: TrackEvolutionEvent): string {
    return event.eventType === 'ORIGINAL_DECISION'
        ? `ORIGINAL:${event.decision?.decisionId ?? 'unknown'}`
        : `TRACK:${event.run?.runId ?? 'unknown'}`;
}

function trackDirectionMeta(posSide: string | null | undefined): { text: string; color: string } {
    const side = posSide?.toLowerCase();
    if (side === 'long') {
        return {text: '多', color: 'var(--pt-bull)'};
    }
    if (side === 'short') {
        return {text: '空', color: 'var(--pt-bear)'};
    }
    return {text: posSide || '', color: '#d9d9d9'};
}

function sortedEvents(state: TrackEvolutionState): TrackEvolutionEvent[] {
    return [...state.eventsByKey.values()].sort((left, right) => {
        if (left.eventTimeMs !== right.eventTimeMs) return left.eventTimeMs - right.eventTimeMs;
        if (left.eventType !== right.eventType) return left.eventType === 'ORIGINAL_DECISION' ? -1 : 1;
        return (left.run?.runId ?? Number(left.decision?.decisionId) ?? 0)
            - (right.run?.runId ?? Number(right.decision?.decisionId) ?? 0);
    });
}

function mergeCandles(previous: KLineBar[], incoming: KLineBar[]): KLineBar[] {
    const byTime = new Map(previous.map(value => [Number(value.time), value]));
    incoming.forEach(value => byTime.set(Number(value.time), value));
    return [...byTime.values()].sort((a, b) => a.time - b.time);
}

function indicatorNumber(value: unknown): number {
    if (value == null || (typeof value === 'string' && value.trim() === '')) return Number.NaN;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function timeframeMs(timeframe: string): number {
    const normalized = timeframe.trim();
    const value = Number.parseInt(normalized, 10);
    if (!Number.isFinite(value)) return 300_000;
    if (normalized.endsWith('m')) return value * 60_000;
    if (normalized.toUpperCase().endsWith('H')) return value * 3_600_000;
    if (normalized.toUpperCase().endsWith('D')) return value * 86_400_000;
    return 300_000;
}

function batchBars(batch: TrackForecastBatch): KLineBar[] {
    const interval = timeframeMs(batch.timeframe);
    return batch.bars
        .map(bar => ({
            time: bar.timestamp ?? batch.evaluationStartTimeMs + Math.max(0, bar.index - 1) * interval,
            open: Number(bar.open),
            high: Number(bar.high),
            low: Number(bar.low),
            close: Number(bar.close),
            volume: 0,
        }))
        .filter(bar => [bar.open, bar.high, bar.low, bar.close].every(Number.isFinite));
}

export function transformIndicators(raw: Record<string, any> | undefined) {
    if (!raw) return undefined;
    const result: Record<string, any[]> = {};
    Object.entries(raw).forEach(([rawType, indicator]) => {
        const type = rawType.toUpperCase();
        const values = indicator?.data?.values
            || indicator?.values
            || (Array.isArray(indicator?.data) ? indicator.data : undefined);
        if (!Array.isArray(values)) return;
        values.forEach((value: any) => {
            const timestampValue = Number(value.timestamp ?? value.time);
            if (!Number.isFinite(timestampValue) || timestampValue <= 0) return;
            const time = timestampValue < 1_000_000_000_000 ? timestampValue * 1000 : timestampValue;
            const periods = value.multiPeriodValues;
            if (!periods || typeof periods !== 'object') {
                const period = Number(indicator?.data?.period ?? indicator?.period ?? 20);
                const key = `${type}_${type.toLowerCase()}_${period}`;
                const point: Record<string, number> = {time};
                if (type === 'MACD') {
                    point.diff = indicatorNumber(value.diff ?? value.dif);
                    point.signal = indicatorNumber(value.dea ?? value.signal);
                    point.histogram = indicatorNumber(value.macd ?? value.histogram);
                } else if (type === 'BOLL') {
                    point.upper = indicatorNumber(value.upper ?? value.upperBand);
                    point.middle = indicatorNumber(value.middle ?? value.middleBand);
                    point.lower = indicatorNumber(value.lower ?? value.lowerBand);
                } else if (type === 'KDJ') {
                    point.k = indicatorNumber(value.k);
                    point.d = indicatorNumber(value.d);
                    point.j = indicatorNumber(value.j);
                } else {
                    point.value = indicatorNumber(value.value);
                }
                const numericValues = Object.entries(point)
                    .filter(([field]) => field !== 'time')
                    .map(([, fieldValue]) => fieldValue);
                if (!numericValues.some(Number.isFinite)) return;
                if (!result[key]) result[key] = [];
                result[key].push(point);
                return;
            }
            Object.entries(periods).forEach(([period, periodValue]: [string, any]) => {
                const key = `${type}_${period}`;
                const point: Record<string, number> = {time};
                if (type === 'MACD' && typeof periodValue === 'object') {
                    point.diff = indicatorNumber(periodValue.diff ?? periodValue.dif);
                    point.signal = indicatorNumber(periodValue.dea ?? periodValue.signal);
                    point.histogram = indicatorNumber(periodValue.histogram ?? periodValue.macd);
                } else if (type === 'BOLL' && typeof periodValue === 'object') {
                    point.upper = indicatorNumber(periodValue.upper ?? periodValue.upperBand);
                    point.middle = indicatorNumber(periodValue.middle ?? periodValue.middleBand);
                    point.lower = indicatorNumber(periodValue.lower ?? periodValue.lowerBand);
                } else if (type === 'KDJ' && typeof periodValue === 'object') {
                    point.k = indicatorNumber(periodValue.k);
                    point.d = indicatorNumber(periodValue.d);
                    point.j = indicatorNumber(periodValue.j);
                } else {
                    point.value = indicatorNumber(
                        typeof periodValue === 'object'
                            ? periodValue.value
                            : periodValue,
                    );
                }
                const numericValues = Object.entries(point)
                    .filter(([field]) => field !== 'time')
                    .map(([, fieldValue]) => fieldValue);
                if (!numericValues.some(Number.isFinite)) return;
                if (!result[key]) result[key] = [];
                result[key].push(point);
            });
        });
    });
    // lightweight-charts requires data sorted ascending by time
    Object.values(result).forEach(arr => arr.sort((a, b) => a.time - b.time));
    return result;
}

function numberList(value: unknown, fallback: number[]): number[] {
    if (!Array.isArray(value)) return fallback;
    const values = value.map(Number).filter(Number.isFinite);
    return values.length ? values : fallback;
}

const TrackEvolutionModal: React.FC<Props> = ({open, onClose, trackUnitId, focusRunId}) => {
    const {phase, state, bootstrap, loadOlder, poll, close} = useTrackEvolution();
    const [marketBars, setMarketBars] = useState<KLineBar[]>([]);
    const [indicators, setIndicators] = useState<Record<string, any[]> | undefined>();
    const [markPrice, setMarkPrice] = useState<number | null>(null);
    const [chartLoading, setChartLoading] = useState(false);
    const [chartError, setChartError] = useState<string | null>(null);
    const [indicatorError, setIndicatorError] = useState<string | null>(null);
    const [visibleIndicators, setVisibleIndicators] = useState(DEFAULT_VISIBLE_INDICATORS);
    const [visibleBatchIds, setVisibleBatchIds] = useState<Set<number>>(new Set());
    const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
    const [highlightedBatchIds, setHighlightedBatchIds] = useState<Set<number>>(new Set());
    const [focusedChartRange, setFocusedChartRange] = useState<{ from: number; to: number } | null>(null);
    const [selectedEventKey, setSelectedEventKey] = useState<string | null>(null);
    const [forecastTooltip, setForecastTooltip] = useState<{
        time: number;
        batches: Array<{ id: string; open: number; high: number; low: number; close: number }>;
    } | null>(null);
    const [showTPSL, setShowTPSL] = useState(false);
    const loadedChartSignature = useRef<string | null>(null);
    const appliedInitialFocusRef = useRef<number | null>(null);
    const eventRefs = useRef<Record<string, HTMLElement | null>>({});

    const events = useMemo(() => sortedEvents(state), [state.eventsByKey]);
    // 预测批次时间倒序：最新（版本号最大）展示在最前
    const batches = useMemo(
        () => [...state.forecastsById.values()].sort((a, b) => b.versionNo - a.versionNo || b.forecastId - a.forecastId),
        [state.forecastsById],
    );
    const latestBatchId = useMemo(() => {
        let latestId: number | null = null;
        let latestVersion = -1;
        batches.forEach(batch => {
            if (batch.versionNo > latestVersion) {
                latestVersion = batch.versionNo;
                latestId = batch.forecastId;
            }
        });
        return latestId;
    }, [batches]);
    const unit = state.unit;
    const chart = state.chart;
    const isActive = phase === 'ACTIVE' && unit?.trackingActive === true && unit?.trackingEndedAtMs == null;
    const isEnded = phase === 'STATIC_ENDED' && unit?.trackingEndedAtMs != null;

    useEffect(() => {
        if (!open) return;
        setMarketBars([]);
        setIndicators(undefined);
        setMarkPrice(null);
        setChartError(null);
        setIndicatorError(null);
        setVisibleBatchIds(new Set());
        setSelectedBatchId(null);
        setHighlightedBatchIds(new Set());
        setFocusedChartRange(null);
        setSelectedEventKey(focusRunId ? `TRACK:${focusRunId}` : null);
        setForecastTooltip(null);
        loadedChartSignature.current = null;
        appliedInitialFocusRef.current = null;
        void bootstrap(trackUnitId, focusRunId);
        return () => close();
    }, [open, trackUnitId, focusRunId, bootstrap, close]);

    useEffect(() => {
        setVisibleBatchIds(previous => {
            const next = new Set(previous);
            batches.forEach(batch => {
                if (batch.validationStatus === 'VALID') next.add(batch.forecastId);
            });
            return next;
        });
    }, [batches]);

    useEffect(() => {
        if (!selectedEventKey || !eventRefs.current[selectedEventKey]) return;
        eventRefs.current[selectedEventKey]?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
    }, [selectedEventKey]);

    const requestChart = useCallback(async (signal?: AbortSignal, replace = false) => {
        if (!unit || !chart) return;
        try {
            if (replace) setChartLoading(true);
            setChartError(null);
            setIndicatorError(null);
            const response = await tradingService.getCompleteChartData({
                instId: unit.instId,
                apiKeyId: unit.apiKeyId,
                timeframe: chart.timeframe,
                limit: 240,
                indicators: ['EMA', 'RSI', 'BOLL', 'MACD', 'KDJ'],
                emaPeriods: numberList(chart.indicatorConfig?.EMA?.periods, [12, 26]),
                rsiPeriods: numberList(chart.indicatorConfig?.RSI?.periods, [9, 14]),
                bollParams: numberList(chart.indicatorConfig?.BOLL?.periods, [20])
                    .map((period, index) => `${period}_${numberList(chart.indicatorConfig?.BOLL?.stdDevs, [2])[index] ?? 2}`),
                macdPeriods: [
                    Number(chart.indicatorConfig?.MACD?.fastPeriod ?? 12),
                    Number(chart.indicatorConfig?.MACD?.slowPeriod ?? 26),
                    Number(chart.indicatorConfig?.MACD?.signalPeriod ?? 9),
                ],
                kdjPeriods: numberList(chart.indicatorConfig?.KDJ?.periods, [9, 14, 21]),
                startMills: chart.suggestedStartTimeMs,
                endMills: chart.suggestedEndTimeMs > 0 ? chart.suggestedEndTimeMs : undefined,
            });
            if (signal?.aborted) return;
            const incoming = (response.candles || []).map(candle => ({
                time: Number(candle.timestamp),
                open: Number(candle.open),
                high: Number(candle.high),
                low: Number(candle.low),
                close: Number(candle.close),
                volume: Number(candle.volume || 0),
            }));
            setMarketBars(previous => replace ? incoming : mergeCandles(previous, incoming));
            if (response.indicators) {
                const transformed = transformIndicators(response.indicators);
                if (transformed && Object.values(transformed).some(values => values.length > 0)) {
                    setIndicators(transformed);
                } else {
                    setIndicators(undefined);
                    setIndicatorError('技术指标返回为空，请检查指标参数或 K 线预热数据');
                }
            } else {
                setIndicators(undefined);
                setIndicatorError('技术指标数据不可用');
            }
            if (isActive && response.markPrice != null) setMarkPrice(Number(response.markPrice));
        } catch (error: any) {
            if (!signal?.aborted) setChartError(error.message || 'K 线加载失败');
        } finally {
            if (!signal?.aborted) setChartLoading(false);
        }
    }, [unit, chart, isEnded, isActive]);

    useEffect(() => {
        if (!open || !unit || !chart) return;
        const signature = `${unit.trackUnitId}:${phase}:${chart.chartDataCutoffTimeMs ?? 'live'}`;
        if (loadedChartSignature.current === signature) return;
        loadedChartSignature.current = signature;
        if (chart.suggestedStartTimeMs > 0 && chart.suggestedEndTimeMs > 0) {
            setFocusedChartRange({
                from: chart.suggestedStartTimeMs,
                to: chart.suggestedEndTimeMs,
            });
        }
        const controller = new AbortController();
        void requestChart(controller.signal, true);
        return () => controller.abort();
    }, [open, unit, chart, phase, requestChart]);

    useSerialPolling({
        enabled: isActive,
        delayMs: 15_000,
        epoch: trackUnitId,
        task: useCallback(signal => poll(signal), [poll]),
    });
    useSerialPolling({
        enabled: isActive,
        delayMs: 15_000,
        epoch: trackUnitId,
        task: useCallback(signal => requestChart(signal), [requestChart]),
    });
    useSerialPolling({
        enabled: isActive,
        delayMs: 5_000,
        epoch: trackUnitId,
        task: useCallback(async signal => {
            if (!unit) return;
            const response = await tradingService.getRealtimePrice(unit.instId, unit.apiKeyId);
            if (!signal.aborted && response.success && response.data) {
                setMarkPrice(Number(response.data.lastPrice));
            }
        }, [unit]),
    });

    const batchColor = useCallback((batch: TrackForecastBatch) => (
        FORECAST_COLORS[Math.max(0, (batch.versionNo || 1) - 1) % FORECAST_COLORS.length]
    ), []);

    const comparisonBatches = useMemo(() => batches
        .filter(batch => batch.validationStatus === 'VALID' && batch.timeframe === chart?.timeframe)
        .map(batch => ({
            id: batch.forecastId,
            color: batchColor(batch),
            data: batchBars(batch),
            visible: visibleBatchIds.has(batch.forecastId),
            emphasized: highlightedBatchIds.has(batch.forecastId)
                || selectedBatchId === batch.forecastId
                || (selectedBatchId == null && batch.forecastId === latestBatchId),
        })), [batches, latestBatchId, chart?.timeframe, batchColor, visibleBatchIds, selectedBatchId, highlightedBatchIds]);

    const comparisonBatchLabels = useMemo(() => Object.fromEntries(
        [...state.forecastsById.values()].map(batch => [String(batch.forecastId), `V${batch.versionNo}`]),
    ), [state.forecastsById]);

    const indicatorConfigs = useMemo<Record<TechnicalIndicator, IndicatorConfig>>(() => {
        const ema = numberList(chart?.indicatorConfig?.EMA?.periods, [12, 26]);
        const rsi = numberList(chart?.indicatorConfig?.RSI?.periods, [9, 14]);
        const boll = numberList(chart?.indicatorConfig?.BOLL?.periods, [20]);
        const kdj = numberList(chart?.indicatorConfig?.KDJ?.periods, [9, 14, 21]);
        const visiblePeriods = (type: string, periods: number[]) => periods
            .filter(period => visibleIndicators[`${type}_${period}`] !== false);
        // periodColors 与 periods 对齐：激活 tag 的 dot 色与其他 K 线图位置（IndicatorChartModal 等）保持一致
        const periodColors = (type: string, periods: number[]) => (INDICATOR_PALETTES[type] ?? [])
            .slice(0, periods.length);
        const macdVisible = Object.entries(visibleIndicators)
            .some(([key, visible]) => key.startsWith('MACD_') && visible);
        return {
            EMA: {
                period: ema[0],
                periods: ema,
                visiblePeriods: visiblePeriods('EMA', ema),
                periodColors: periodColors('EMA', ema),
            },
            SMA: {period: 20, periods: [], visiblePeriods: []},
            WMA: {period: 20, periods: [], visiblePeriods: []},
            RSI: {
                period: rsi[0],
                periods: rsi,
                visiblePeriods: visiblePeriods('RSI', rsi),
                periodColors: periodColors('RSI', rsi),
            },
            BOLL: {
                period: boll[0],
                periods: boll,
                visiblePeriods: visiblePeriods('BOLL', boll),
                periodColors: periodColors('BOLL', boll),
                stdDevs: numberList(chart?.indicatorConfig?.BOLL?.stdDevs, [2]),
            },
            MACD: {
                period: macdVisible ? Number(chart?.indicatorConfig?.MACD?.fastPeriod ?? 12) : 0,
                fastPeriod: macdVisible ? Number(chart?.indicatorConfig?.MACD?.fastPeriod ?? 12) : 0,
                slowPeriod: macdVisible ? Number(chart?.indicatorConfig?.MACD?.slowPeriod ?? 26) : 0,
                signalPeriod: macdVisible ? Number(chart?.indicatorConfig?.MACD?.signalPeriod ?? 9) : 0,
                periods: [],
                visiblePeriods: macdVisible ? [1] : [],
                periodColors: [INDICATOR_PALETTES.MACD[0]],
            },
            KDJ: {
                period: kdj[0],
                periods: kdj,
                visiblePeriods: visiblePeriods('KDJ', kdj),
                periodColors: periodColors('KDJ', kdj),
            },
            CCI: {period: 20, periods: [], visiblePeriods: []},
            ATR: {period: 14, periods: [], visiblePeriods: []},
            OBV: {period: 20, periods: [], visiblePeriods: []},
            ADX: {period: 14, periods: [], visiblePeriods: []},
        };
    }, [chart?.indicatorConfig, visibleIndicators]);

    const toggleIndicator = useCallback((type: TechnicalIndicator) => {
        setVisibleIndicators(previous => {
            const keys = Object.keys(previous).filter(key => key.startsWith(`${type}_`));
            if (!keys.length) return previous;
            const hide = keys.some(key => previous[key]);
            return keys.reduce((next, key) => ({...next, [key]: !hide}), {...previous});
        });
    }, []);

    const updateIndicatorVisibility = useCallback((
        type: TechnicalIndicator,
        config: Partial<IndicatorConfig>,
    ) => {
        if (!config.visiblePeriods) return;
        setVisibleIndicators(previous => {
            const next = {...previous};
            const periods = indicatorConfigs[type]?.periods || [];
            periods.forEach(period => {
                next[`${type}_${period}`] = config.visiblePeriods!.includes(period);
            });
            return next;
        });
    }, [indicatorConfigs]);

    const clearIndicators = useCallback(() => {
        // 一键清理：所有技术指标（含各周期）全部恢复非激活（2026-08-01）
        setVisibleIndicators(previous => Object.fromEntries(
            Object.keys(previous).map(key => [key, false]),
        ));
    }, []);

    const selectEvent = useCallback((event: TrackEvolutionEvent, focusChart = true) => {
        const key = eventKey(event);
        setSelectedEventKey(key);
        const forecastIds = [event.run?.inputForecastId, event.run?.outputForecastId]
            .filter((id): id is number => id != null);
        const output = event.run?.outputForecastId ?? event.run?.inputForecastId ?? null;
        setSelectedBatchId(output);
        setHighlightedBatchIds(new Set(forecastIds));
        setVisibleBatchIds(previous => new Set([...previous, ...forecastIds]));
        const linkedBatches = forecastIds
            .map(id => state.forecastsById.get(id))
            .filter((batch): batch is TrackForecastBatch => batch != null);
        const starts = [
            event.run?.comparisonStartTimeMs,
            ...linkedBatches.map(batch => batch.evaluationStartTimeMs),
        ].filter((value): value is number => value != null);
        const ends = [
            event.run?.comparisonEndTimeMs,
            ...linkedBatches.map(batch => batch.evaluationEndTimeMs),
        ].filter((value): value is number => value != null);
        if (focusChart && starts.length && ends.length) {
            const padding = timeframeMs(chart?.timeframe || '5m') * 2;
            setFocusedChartRange({
                from: Math.min(...starts) - padding,
                to: Math.max(...ends) + padding,
            });
        }
    }, [state.forecastsById, chart?.timeframe]);

    const selectBatch = useCallback((batch: TrackForecastBatch, linkEvent = true) => {
        setSelectedBatchId(batch.forecastId);
        setHighlightedBatchIds(new Set([batch.forecastId]));
        const padding = timeframeMs(batch.timeframe) * 2;
        setFocusedChartRange({
            from: batch.evaluationStartTimeMs - padding,
            to: batch.evaluationEndTimeMs + padding,
        });
        // 图例点击只控制 K 线图展示（选中/聚焦视口），不联动事件列表滑动（2026-08-01）
        if (!linkEvent) return;
        const key = batch.sourceTrackRunId == null
            ? events.find(event => event.eventType === 'ORIGINAL_DECISION')
            : events.find(event => event.run?.runId === batch.sourceTrackRunId);
        if (key) setSelectedEventKey(eventKey(key));
    }, [events]);

    useEffect(() => {
        if (focusRunId == null || appliedInitialFocusRef.current === focusRunId) return;
        const focusEvent = events.find(event => event.run?.runId === focusRunId);
        if (!focusEvent) return;
        appliedInitialFocusRef.current = focusRunId;
        // 从 Prompt 历史打开时只标记来源记录，不自动套用约 10 根 K 线的单轮比较窗口。
        // 默认视口必须保留完整演进范围；用户之后主动点击时间线时才聚焦该轮。
        selectEvent(focusEvent, false);
    }, [focusRunId, events, selectEvent]);

    const headerTrackCount = state.page?.totalTrackCount ?? events.filter(event => event.eventType === 'TRACK').length;
    const loadedTrackCount = events.filter(event => event.eventType === 'TRACK').length;
    // 有效批次集合："显示全部"的目标集合，其激活状态 = 全部有效批次均可见（2026-08-01）
    const validBatchIds = useMemo(
        () => new Set(batches.filter(batch => batch.validationStatus === 'VALID').map(batch => batch.forecastId)),
        [batches],
    );
    const allVisible = validBatchIds.size > 0 && [...validBatchIds].every(id => visibleBatchIds.has(id));
    const allHidden = visibleBatchIds.size === 0;
    const hasTPSL = (state.originalAction?.takeProfit != null) || (state.originalAction?.stopLoss != null);
    const chartReferenceLines = useMemo(() => ({
        avgPx: unit?.entryAvgPrice == null ? undefined : Number(unit.entryAvgPrice),
        takeProfitPx: showTPSL && state.originalAction?.takeProfit != null
            ? Number(state.originalAction.takeProfit)
            : undefined,
        stopLossPx: showTPSL && state.originalAction?.stopLoss != null
            ? Number(state.originalAction.stopLoss)
            : undefined,
        cTime: state.originalDecision?.createdTime ?? undefined,
        posSide: unit?.posSide,
    }), [
        unit?.entryAvgPrice,
        unit?.posSide,
        showTPSL,
        state.originalAction?.takeProfit,
        state.originalAction?.stopLoss,
        state.originalDecision?.createdTime,
    ]);
    const refreshAll = useCallback(() => {
        loadedChartSignature.current = null;
        void bootstrap(trackUnitId, focusRunId);
    }, [bootstrap, trackUnitId, focusRunId]);

    return (
        <Modal
            className="track-evolution-modal"
            open={open}
            onCancel={onClose}
            footer={null}
            width="90%"
            destroyOnHidden
            title={
                <div className="track-evolution-title">
                    <Space wrap>
                        <CompassOutlined/>
                        <span>Track 决策演进</span>
                        {unit && (() => {
                            const direction = trackDirectionMeta(unit.posSide);
                            return (
                                <Tag
                                    color={unit.posSide === 'long' ? 'green' : unit.posSide === 'short' ? 'red' : undefined}
                                    style={{color: direction.color}}
                                >
                                    {unit.instId}
                                </Tag>
                            );
                        })()}
                        {unit && <Tag
                            color={unit.trackingActive ? 'processing' : unit.status === 'PENDING_FILL' ? 'default' : 'success'}>{unit.statusLabel}</Tag>}
                        <Text type="secondary">{state.originalDecision ? 1 : 0} 条原始决策 · {headerTrackCount} 条
                            Track</Text>
                    </Space>
                    <Tooltip title="刷新演进与图表">
                        <Button type="text" icon={<ReloadOutlined/>} onClick={refreshAll}/>
                    </Tooltip>
                </div>
            }
        >
            {state.loading && events.length === 0 ? (
                <div className="track-evolution-loading"><Spin size="large"/></div>
            ) : state.error ? (
                <Alert
                    type="error"
                    showIcon
                    message="Track 演进加载失败"
                    description={state.error}
                    action={<Button onClick={() => void bootstrap(trackUnitId, focusRunId)}>重试</Button>}
                />
            ) : !unit ? (
                <Empty description="暂无 Track 演进数据"/>
            ) : (
                <>
                    <section className="track-evolution-chart-area">
                        <div className="track-evolution-chart-toolbar">
                            <Space wrap>
                                {isActive && <Tag color="processing">实时更新中</Tag>}
                                {phase === 'STATIC_PENDING' && <Tag>等待开仓成交 · 当前为静态数据</Tag>}
                                {isEnded && (
                                    <Tag color="success">
                                        {focusRunId != null
                                            ? '快照'
                                            : `Track 已结束 · 数据截至 ${new Date(chart?.chartDataCutoffTimeMs || unit.trackingEndedAtMs!).toLocaleString()}`
                                        }
                                    </Tag>
                                )}
                                {chart?.chartDataCutoffEstimated && <Tag color="warning">历史截止时间为迁移估算</Tag>}
                                {/*{chart?.indicatorConfigSource === 'DEFAULT' && <Tag>使用默认指标参数</Tag>}*/}
                                {chart?.timeframe && (
                                    <Tag style={{
                                        borderColor: '#13c2c2',
                                        color: '#13c2c2',
                                        background: 'rgba(19, 194, 194, 0.06)',
                                    }}>{chart.timeframe}</Tag>
                                )}
                                {chart != null && chart.suggestedStartTimeMs > 0 && chart.suggestedEndTimeMs > 0 && (
                                    <Text type="secondary" style={{fontSize: 12}}>
                                        {new Date(chart.suggestedStartTimeMs).toLocaleString('zh-CN', {
                                            month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
                                        })} ~ {new Date(chart.suggestedEndTimeMs).toLocaleString('zh-CN', {
                                        month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
                                    })}
                                    </Text>
                                )}
                                <Segmented
                                    size="small"
                                    value={showTPSL ? 'show' : 'hide'}
                                    options={[
                                        {label: 'TP/SL', value: 'show'},
                                        {label: '隐藏', value: 'hide'},
                                    ]}
                                    disabled={!hasTPSL}
                                    onChange={value => setShowTPSL(value === 'show')}
                                />
                            </Space>
                        </div>
                        <div className="track-evolution-indicator-bar">
                            <CompactIndicatorBar
                                selectedIndicators={TRACK_INDICATOR_TYPES}
                                indicatorConfigs={indicatorConfigs}
                                onToggleIndicator={toggleIndicator}
                                onUpdateIndicatorConfig={updateIndicatorVisibility}
                                onClearAllIndicators={clearIndicators}
                                readonly
                                availableTypes={TRACK_INDICATOR_TYPES}
                            />
                        </div>

                        <div className="track-evolution-forecast-legend">
                            <div className="track-evolution-legend-actions">
                                <Text strong>预测批次</Text>
                                <Tooltip title="显示全部">
                                    <span
                                        className={`track-evolution-legend-action${allVisible ? ' active' : ''}`}
                                        onClick={() => setVisibleBatchIds(new Set(validBatchIds))}
                                    >
                                        {allVisible ? <CheckSquareFilled/> : <CheckSquareOutlined/>}
                                    </span>
                                </Tooltip>
                                <Tooltip title="全部隐藏">
                                    <span
                                        className={`track-evolution-legend-action${allHidden ? ' active' : ''}`}
                                        onClick={() => setVisibleBatchIds(new Set())}
                                    >
                                        {allHidden ? <MinusSquareFilled/> : <MinusSquareOutlined/>}
                                    </span>
                                </Tooltip>
                            </div>
                            <div className="track-evolution-legend-items">
                                {batches.map(batch => {
                                    const invalid = batch.validationStatus !== 'VALID' || batch.timeframe !== chart?.timeframe;
                                    return (
                                        <div
                                            key={batch.forecastId}
                                            className={`track-evolution-legend-item${selectedBatchId === batch.forecastId ? ' selected' : ''}`}
                                            onClick={() => !invalid && selectBatch(batch, false)}
                                        >
                                            <Checkbox
                                                checked={!invalid && visibleBatchIds.has(batch.forecastId)}
                                                disabled={invalid}
                                                onClick={event => event.stopPropagation()}
                                                onChange={event => setVisibleBatchIds(previous => {
                                                    const next = new Set(previous);
                                                    event.target.checked ? next.add(batch.forecastId) : next.delete(batch.forecastId);
                                                    return next;
                                                })}
                                            />
                                            <span className="track-evolution-legend-swatch"
                                                  style={{background: batchColor(batch)}}/>
                                            <span>V{batch.versionNo}{batch.sourceTrackRunId ? ` #${batch.sourceTrackRunId}` : ''}</span>
                                            {lifecycleStatusIcon(batch.lifecycleStatus)}
                                            {invalid && (
                                                <Tooltip title={batch.validationErrors?.join('；') || undefined}>
                                                    <Tag color="warning">
                                                        {batch.timeframe !== chart?.timeframe ? '时间帧不一致' : '预测无效'}
                                                    </Tag>
                                                </Tooltip>
                                            )}
                                            {!invalid && (() => {
                                                const soloVisible = visibleBatchIds.size === 1 && visibleBatchIds.has(batch.forecastId);
                                                return (
                                                    <Tooltip title={soloVisible ? '取消仅看' : '仅看'}>
                                                        <span
                                                            className={`track-evolution-legend-eye${soloVisible ? ' active' : ''}`}
                                                            onClick={event => {
                                                                event.stopPropagation();
                                                                if (soloVisible) {
                                                                    // 取消仅看：恢复显示全部有效批次
                                                                    setVisibleBatchIds(new Set(batches
                                                                        .filter(b => b.validationStatus === 'VALID')
                                                                        .map(b => b.forecastId)));
                                                                } else {
                                                                    setVisibleBatchIds(new Set([batch.forecastId]));
                                                                    selectBatch(batch, false);
                                                                }
                                                            }}
                                                        >
                                                            {soloVisible ? <EyeFilled/> : <EyeOutlined/>}
                                                        </span>
                                                    </Tooltip>
                                                );
                                            })()}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {batches.length === 0 && (
                            <Alert type="info" showIcon message="原始决策未生成有效预测"/>
                        )}

                        {chartError && <Alert type="error" showIcon message="K 线加载失败" description={chartError}
                                              action={<Button size="small"
                                                              onClick={() => void requestChart(undefined, true)}>重试</Button>}/>}
                        {indicatorError && <Alert type="warning" showIcon message={indicatorError}/>}
                        {state.warningCodes?.length > 0 && <Alert type="warning" showIcon message="部分关联数据不可用"
                                                                  description={state.warningCodes.join('、')}/>}

                        <div className="track-evolution-chart">
                            {chartLoading && marketBars.length === 0 ? <Spin size="large"/> : marketBars.length > 0 ? (
                                <LightweightCandlestickChart
                                    data={marketBars}
                                    comparisonBatches={comparisonBatches}
                                    comparisonBatchLabels={comparisonBatchLabels}
                                    focusTimeRange={focusedChartRange}
                                    timeFrame={chart?.timeframe}
                                    markPrice={isActive ? markPrice : null}
                                    indicators={indicators}
                                    visibleIndicators={visibleIndicators}
                                    referenceLines={chartReferenceLines}
                                    maxVisibleBars={100}
                                    lockRightEdge={isEnded}
                                    preserveViewportOnDataUpdate
                                    onComparisonTooltip={setForecastTooltip}
                                />
                            ) : <Empty description="暂无 K 线数据"/>}
                        </div>

                        {forecastTooltip?.batches.length ? (
                            <div className="track-evolution-forecast-tooltip">
                                <Text type="secondary">{new Date(forecastTooltip.time).toLocaleString()}</Text>
                                {forecastTooltip.batches.map(value => {
                                    const batch = state.forecastsById.get(Number(value.id));
                                    return (
                                        <span key={value.id} style={{color: batch ? batchColor(batch) : undefined}}>
                      V{batch?.versionNo ?? value.id} O {value.open} H {value.high} L {value.low} C {value.close}
                    </span>
                                    );
                                })}
                            </div>
                        ) : null}

                    </section>

                    <section className="track-evolution-timeline-section">
                        <TrackEvolutionTimeline
                            events={events}
                            page={state.page}
                            selectedEventKey={selectedEventKey}
                            focusRunId={focusRunId ?? null}
                            onSelectEvent={key => {
                                const event = events.find(item => eventKey(item) === key);
                                if (event) selectEvent(event);
                            }}
                            onLoadOlder={() => void loadOlder()}
                            loadingOlder={state.loadingOlder}
                        />
                        <div className="track-evolution-event-list">
                            {events.map((event, index) => {
                                const key = eventKey(event);
                                const batch = event.run?.outputForecastId == null ? null : state.forecastsById.get(event.run.outputForecastId);
                                return (
                                    <div
                                        key={key}
                                        ref={node => {
                                            eventRefs.current[key] = node;
                                        }}
                                        className="track-evolution-event-row"
                                    >
                                        <div className="track-evolution-event-rail">
                                            <span>{index + 1}</span>
                                        </div>
                                        <TrackEvolutionEventCard
                                            event={event}
                                            originalAction={state.originalAction}
                                            selected={selectedEventKey === key}
                                            forecastColor={batch ? batchColor(batch) : undefined}
                                            hasInitialForecast={batches.some(item => item.versionNo === 1 && item.sourceTrackRunId == null)}
                                            onSelect={() => selectEvent(event)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        {loadedTrackCount === 0 && <Empty description="暂无 Track 记录"/>}
                        {state.page?.hasMore && (
                            <div className="track-evolution-load-more">
                                <Button loading={state.loadingOlder} onClick={() => void loadOlder()}>
                                    加载更早记录（已加载 {loadedTrackCount}/{state.page.totalTrackCount}）
                                </Button>
                            </div>
                        )}
                    </section>
                </>
            )}
        </Modal>
    );
};

export default TrackEvolutionModal;
